import React from 'react';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { FiCheck } from 'react-icons/fi';

function ProgramsPanel({ data }) {
  const { t } = useTranslation();

  // Get translated content
  const title = t('programLifecycle.title', { defaultValue: 'Program Lifecycle Support' });
  const subtitle = t('programLifecycle.subtitle', { defaultValue: 'From specification translation to witness-ready acceptance — executed with controlled baselines, traceability, and evidence closure.' });

  // Get translated cards
  const translatedCards = t('programLifecycle.cards', { returnObjects: true });
  
  // Default program lifecycle cards
  const defaultPrograms = [
    {
      title: 'Specification Translation & System Engineering',
      description: 'Customer specifications are decomposed into interfaces, derived constraints, and acceptance criteria—so engineering starts from a controlled baseline.',
      signals: ['Requirement closure matrix', 'Assumptions control', 'Interface definition']
    },
    {
      title: 'Prototype-to-Production Readiness',
      description: 'Prototype builds are executed with controlled configuration so production transition is predictable, repeatable, and acceptance-friendly.',
      signals: ['As-built traceability', 'Rework control', 'Manufacturing readiness discipline']
    },
    {
      title: 'FAT/ATP & Witness Readiness',
      description: 'Acceptance planning is organized as an evidence pack—test intent, setup readiness, results reporting—aligned to closure criteria.',
      signals: ['ATP/TP', 'FAT readiness checklist', 'Witness testing support']
    }
  ];

  // Use translated cards if available, otherwise defaults
  const programs = Array.isArray(translatedCards) ? translatedCards : defaultPrograms;

  return (
    <div 
      id="programs" 
      className="panel panel-white scroll-mt-20" 
      data-testid="programs-panel"
    >
      <div className="panel-content">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6, ease: "easeOut" }}
          className="text-center mb-12"
        >
          <h2 className="text-3xl md:text-4xl font-bold mb-3 heading-premium">
            {title}
          </h2>
          <p className="text-base md:text-lg max-w-3xl mx-auto subheading-premium">
            {subtitle}
          </p>
        </motion.div>

        <div className="grid md:grid-cols-3 gap-6 items-stretch">
          {programs.map((program, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.5, ease: "easeOut", delay: index * 0.1 }}
              className="card-premium p-6 flex flex-col h-full"
              data-testid={`program-card-${index}`}
            >
              <h3 className="text-lg font-bold mb-3 leading-tight" style={{ color: '#101957' }}>
                {program.title}
              </h3>
              <p className="text-sm leading-relaxed mb-4 flex-grow" style={{ color: '#475569' }}>
                {program.description}
              </p>
              
              {/* Signals - Aligned to bottom with mt-auto */}
              {program.signals && program.signals.length > 0 && (
                <div className="pt-3 border-t mt-auto" style={{ borderColor: 'rgba(16, 25, 87, 0.08)' }}>
                  <div className="flex flex-wrap gap-x-3 gap-y-1">
                    {program.signals.map((signal, signalIndex) => (
                      <span 
                        key={signalIndex}
                        className="inline-flex items-center text-xs"
                        style={{ color: '#64748b' }}
                      >
                        <FiCheck className="mr-1 flex-shrink-0" style={{ color: '#101957' }} />
                        {signal}
                      </span>
                    ))}
                  </div>
                </div>
              )}
            </motion.div>
          ))}
        </div>
      </div>
    </div>
  );
}

export default ProgramsPanel;
